import * as L from 'leaflet';
import { ElementRef, Injectable } from '@angular/core';
import {
  ParamsCreateCircle,
  ParamsCreateCircleMarker,
  ParamsCreateMap,
  ParamsCreateMarker,
  ParamsCreatePolygon,
  ParamsCreatePolyline,
  ParamsCreatePolylineDecorator,
  ResponseCreateCircle,
  ResponseCreateCircleMarker,
  ResponseCreateMarker,
  ResponseCreatePolygon,
  ResponseCreatePolyline,
  ResponseCreatePolylineDecorator,
} from '../models/manager-leaflet.model';
import { getBaseLayers } from '../helpers/get-base-layers.helper';
import { createPolyline } from '../helpers/create-polyline.helper';
import { createMarkerWithIcon } from '../helpers/create-marker-with-icon.helper';
import { createCircleMarker } from '../helpers/create-circle-marker.helper';
import { createPolygon } from '../helpers/create-polygon.helper';
import { createCircle } from '../helpers/create-circle.helper';
import { createPolylineDecorator } from '../helpers/create-polyline-decorator.helper';

@Injectable({
  providedIn: 'root',
})
export class LeafletMapService {
  private readonly defaultConfig: ParamsCreateMap = {
    initialCenter: [-9.8990023, -76.1356287],
    initialZoom: 6,
  };

  constructor() {}

  newMapLeaflet(
    container: ElementRef<HTMLElement>,
    config?: Partial<ParamsCreateMap>
  ): L.Map {
    const finalConfig: ParamsCreateMap = { ...this.defaultConfig, ...config };

    const map = L.map(container.nativeElement).setView(
      finalConfig.initialCenter,
      finalConfig.initialZoom
    );

    const baseLayers = getBaseLayers();
    baseLayers.OpenStreetMap.addTo(map);
    L.control.layers(baseLayers).addTo(map);
    return map;
  }

  centerMapLeaflet(waitTime: number, map: L.Map) {
    setTimeout(() => {
      map.invalidateSize({ animate: true, duration: 0.3 });
    }, waitTime);
  }

  centerPolylineLeaflet(polyline: L.Polyline, map: L.Map, waitTime?: number) {
    const bounds = polyline.getBounds();
    const waitTimeFinal = waitTime || 0;
    map.fitBounds(bounds);
    setTimeout(() => {
      map.invalidateSize({ animate: true, duration: 0.3 });
    }, waitTimeFinal);
  }

  createPolylineLeaflet(
    coordinates: { latitude: number; longitude: number }[],
    options?: L.PolylineOptions
  ): ResponseCreatePolyline {
    const params: ParamsCreatePolyline = {
      coordinates,
      options,
    };
    return createPolyline(params);
  }

  createPolylineDecoratorLeaflet(
    polyline: L.Polyline,
    options?: L.PolylineDecoratorOptions
  ): ResponseCreatePolylineDecorator {
    const params: ParamsCreatePolylineDecorator = {
      polyline,
      options,
    };
    return createPolylineDecorator(params);
  }

  createPolygonLeaflet(
    coordinates: { latitude: number; longitude: number }[],
    options?: L.PolylineOptions
  ): ResponseCreatePolygon {
    const params: ParamsCreatePolygon = {
      coordinates,
      options,
    };
    return createPolygon(params);
  }

  centerPolygonLeaflet(polygon: L.Polygon, map: L.Map, waitTime?: number) {
    const bounds = polygon.getBounds();
    const waitTimeFinal = waitTime || 0;
    map.fitBounds(bounds);
    setTimeout(() => {
      map.invalidateSize({ animate: true, duration: 0.3 });
    }, waitTimeFinal);
  }

  createMarkerWithIconLeaflet(
    coordinates: { latitude: number; longitude: number },
    icon: L.Icon<L.IconOptions> | L.DivIcon,
    options?: L.MarkerOptions,
    animation: boolean = true
  ): ResponseCreateMarker {
    const params: ParamsCreateMarker = {
      coordinates,
      icon,
      options,
      animation,
    };
    return createMarkerWithIcon(params);
  }

  centerMarkerLeaflet(
    marker: L.Marker,
    map: L.Map,
    waitTime?: number,
    zoom?: number
  ) {
    const waitTimeFinal = waitTime || 0;
    map.setView(marker.getLatLng(), zoom || 14);
    setTimeout(() => {
      map.invalidateSize({ animate: true, duration: 0.3 });
    }, waitTimeFinal);
  }

  createCircleMarkerLeaflet(
    coordinates: { latitude: number; longitude: number },
    options?: L.CircleMarkerOptions
  ): ResponseCreateCircleMarker {
    const params: ParamsCreateCircleMarker = {
      coordinates,
      options,
    };
    return createCircleMarker(params);
  }

  centerCircleMarkerLeaflet(
    circleMarker: L.CircleMarker,
    map: L.Map,
    waitTime?: number
  ) {
    const waitTimeFinal = waitTime || 0;
    map.setView(circleMarker.getLatLng(), 18);
    setTimeout(() => {
      map.invalidateSize({ animate: true, duration: 0.3 });
    }, waitTimeFinal);
  }

  createCircleLeaflet(
    coordinates: { latitude: number; longitude: number },
    radius: number,
    options?: L.PathOptions
  ): ResponseCreateCircle {
    const params: ParamsCreateCircle = {
      coordinates,
      radius,
      options,
    };
    return createCircle(params);
  }

  centerCircleLeaflet(circle: L.Circle, map: L.Map, waitTime?: number) {
    const waitTimeFinal = waitTime || 0;
    map.panTo(circle.getLatLng());
    setTimeout(() => {
      map.invalidateSize({ animate: true, duration: 0.3 });
    }, waitTimeFinal);
  }
}
